import type { IntlShape } from 'react-intl'
import { getConnectionRefundPolicyDetails } from 'components/Common/CancellationPolicies/utils/getAlwaysOnConnectionRefundPolicyDetails'
import { Refundable } from 'components/Common/CancellationPolicies/utils/getCancellationPolicyStatus'
import { CANCELLATION_POLICY_DAYS, SEVEN_DAYS_AFTER_PURCHASE } from 'constants/cancellationPolicies'
import { INTL_PICKER_DATE_FORMAT } from 'constants/dateFormats'
import { isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'

import moment from 'moment'

interface BuildPolicyOptions {
  cancellationPolicy: App.OfferCancellationPolicy
  checkInDate?: moment.Moment
  propertyTimezoneOffset?: number
}

function buildAlwaysOnCancellationPolicies(intl: IntlShape, {
  cancellationPolicy,
  checkInDate,
  propertyTimezoneOffset,
}: BuildPolicyOptions): Array<TermsAndConditions.CancellationPolicy> {
  const days = cancellationPolicy.type ? CANCELLATION_POLICY_DAYS[cancellationPolicy.type] ?? 0 : 0
  let cancellationDeadline: moment.Moment | undefined
  const startBasedOnCheckIn = checkInDate ? ((propertyTimezoneOffset ? moment(checkInDate).utcOffset(propertyTimezoneOffset).subtract(days, 'days') : moment(checkInDate).subtract(days, 'days'))) : undefined
  const startBasedOnToday = propertyTimezoneOffset ? moment().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') : moment().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')

  if (cancellationPolicy.type === 'post-purchase-seven-days') {
    if (!startBasedOnCheckIn) {
      cancellationDeadline = startBasedOnToday
    } else {
      cancellationDeadline = startBasedOnToday.isBefore(startBasedOnCheckIn) ? startBasedOnToday : startBasedOnCheckIn
    }
  } else {
    cancellationDeadline = startBasedOnCheckIn
  }

  const formattedCancellationDeadline = cancellationDeadline ? intlDateFormatter(cancellationDeadline, INTL_PICKER_DATE_FORMAT) : undefined
  const refundable = cancellationPolicy.type !== 'non-refundable' && (!cancellationDeadline || cancellationDeadline?.isAfter(moment()))

  if (refundable) {
    return [
      {
        title: intl.formatMessage({
          id: 'y8KSiTm59aHpgHR2kzoT',
          defaultMessage: 'Cancel until {date}',
        }, { date: formattedCancellationDeadline }),
        description: intl.formatMessage({
          id: 'Wd01Z7LE2okXl8KKot5N',
          defaultMessage: '100% refund to accommodation purchase price',
        }),
      },
      {
        title: intl.formatMessage({
          id: '6t9puY3zLbn4Mx4GK4NO',
          defaultMessage: 'Cancel after {date}',
        }, { date: formattedCancellationDeadline }),
        description: intl.formatMessage({
          id: 'QwrtL38ismKtzB1O1M_e',
          defaultMessage: 'Non-refundable',
        }),
      },
    ]
  }

  return [{
    title: intl.formatMessage({
      id: 'CqcJy8uJVnUPQkKLMYGQ',
      defaultMessage: 'Non-refundable',
    }),
    description: intl.formatMessage({
      id: '2jKw4YUCEyfTQiJvxT9A',
      defaultMessage: 'This is a non-refundable room',
    }),
  }]
}

function buildConnectionCancellationPolicies(intl: IntlShape, rateCancellationPolicies: App.ConnectionCancellationPolicies | undefined, checkInDate?: string): Array<TermsAndConditions.CancellationPolicy> {
  if (!rateCancellationPolicies || rateCancellationPolicies.policies.length === 0) {
    return [{
      title: intl.formatMessage({
        id: 'xRbmhtj-Ks-qTb3XLvTH',
        defaultMessage: 'Non-refundable',
      }),
      description: intl.formatMessage({
        id: 'LLJA8ZuFkckx9depSQdf',
        defaultMessage: 'This is a non-refundable room',
      }),
    }]
  }

  const { refundable, deadline, timezone } = getConnectionRefundPolicyDetails(rateCancellationPolicies, checkInDate)
  const formattedDeadline = deadline ? intlDateFormatter(deadline, INTL_PICKER_DATE_FORMAT, undefined, timezone) : null

  if (refundable === Refundable.Non) {
    return [{
      title: intl.formatMessage({
        id: 'xRbmhtj-Ks-qTb3XLvTH',
        defaultMessage: 'Non-refundable',
      }),
      description: intl.formatMessage({
        id: 'LLJA8ZuFkckx9depSQdf',
        defaultMessage: 'This is a non-refundable room',
      }),
    }]
  }

  const policies: Array<TermsAndConditions.CancellationPolicy> = []

  if (formattedDeadline && refundable === Refundable.Fully) {
    policies.push({
      title: intl.formatMessage({
        id: 'zZQtkKTtE6HpSWZ0jyz_',
        defaultMessage: 'Cancel until {date}',
      }, { date: formattedDeadline }),
      description: intl.formatMessage({
        id: '3tEqLbTqQlOLcOiDJqm6',
        defaultMessage: '100% refund to accommodation purchase price',
      }),
    })

    policies.push({
      title: intl.formatMessage({
        id: '7Hg7q03HJ1RTklTVTOp5',
        defaultMessage: 'Cancel after {date}',
      }, { date: formattedDeadline }),
      description: intl.formatMessage({
        id: 'W6TCMTNfRuw8ETBeV7Xl',
        defaultMessage: 'Non-refundable',
      }),
    })
  }

  return policies
}

function getDynamicRateCancellationPolicies(
  itemView: App.Checkout.LEAccommodationItemView,
  offerAvailableRates: App.OfferAvailableRatesByOccupancy,
): App.ConnectionCancellationPolicies | undefined {
  if (!isInstantBookingLEHotelItem(itemView.item) || !itemView.occupancy) {
    return
  }

  const { checkIn, checkOut } = itemView.item
  const rooms = itemView.occupancy ? [itemView.occupancy] : []

  const key = buildAvailableRateKey(checkIn, checkOut, rooms)

  const { rates: offerAvailableRate } = offerAvailableRates[key] ?? {}

  if (!offerAvailableRate) return

  const uniqueKey = itemView.pkg?.uniqueKey
  return offerAvailableRate?.find(rate => rate.packageUniqueKey === uniqueKey)?.cancellationPolicies
}

export function isLPCItemNonRefundable(
  itemView: App.Checkout.LEAccommodationItemView,
  useDynamicCancellationPolicies: boolean,
  offerAvailableRates?: App.OfferAvailableRatesByOccupancy,
): boolean {
  const ignoreDynamic = itemView.pkg?.ignoreDynamicCancellationPolicy ?? false

  const canUseDynamic = useDynamicCancellationPolicies && !ignoreDynamic
  const staticPolicy = itemView.pkg?.roomRate?.cancellationPolicy

  const hasStaticNonRefundable = staticPolicy?.type === 'non-refundable'

  if (hasStaticNonRefundable) return true

  if (canUseDynamic) {
    const dynamicPolicies = offerAvailableRates ? getDynamicRateCancellationPolicies(itemView, offerAvailableRates) : undefined
    const checkInDate = isInstantBookingLEHotelItem(itemView.item)
      ? itemView.item.checkIn
      : undefined

    const { refundable } = dynamicPolicies ?
        getConnectionRefundPolicyDetails(dynamicPolicies, checkInDate)
      : {}

    const isNonRefundable = refundable ? refundable !== Refundable.Fully : false

    return isNonRefundable
  }

  return false
}

export function getLPCItemCancellationPolicies(
  intl: IntlShape,
  itemView: App.Checkout.LEAccommodationItemView,
  property?: App.OfferProperty,
  offerAvailableRates?: App.OfferAvailableRatesByOccupancy,
): Array<TermsAndConditions.CancellationPolicy> {
  const ignoreDynamicCancellationPolicy = itemView.pkg?.ignoreDynamicCancellationPolicy ?? false
  const useDynamicCancellationPolicies = property?.useDynamicCancellationPolicies && !ignoreDynamicCancellationPolicy

  const checkInDate = isInstantBookingLEHotelItem(itemView.item) ? itemView.item.checkIn : undefined

  const cancellationPolicy = itemView.pkg?.roomRate?.cancellationPolicy

  if (useDynamicCancellationPolicies) {
    const dynamicPolicies = offerAvailableRates ? getDynamicRateCancellationPolicies(itemView, offerAvailableRates) : undefined
    return buildConnectionCancellationPolicies(intl, dynamicPolicies, checkInDate)
  }

  if (!cancellationPolicy) {
    return []
  }

  return buildAlwaysOnCancellationPolicies(intl, {
    cancellationPolicy,
    checkInDate: checkInDate ? moment(checkInDate) : undefined,
    propertyTimezoneOffset: property?.timezoneOffset,
  })
}
